/**
 * What the Item Journey says about an item, beyond its list of events:
 * which events are key moments, what state the item was in and for how long,
 * and the one-line story at the top of the page.
 *
 * Canon (01-terminology-and-glossary.md → Lineage): Lineage answers "How did
 * this item get to where it is today?", and its default view surfaces
 * high-signal events with a full view on request. Everything here is derived
 * from the events the page already shows — nothing new is read or stored.
 */
import type { Tone } from '../../../components/mono/primitives'
import type { JourneyEvent, JourneyEventType } from '../ItemJourneyPage'

// ─── Key moments ──────────────────────────────────────────────────────────────

/** Meaningful, but not what you came to the map to see. Shown under "Everything". */
const BACKGROUND_TYPES: ReadonlySet<JourneyEventType> = new Set<JourneyEventType>([
  'classification_changed',
  'parent_changed',
  'dedupe_reviewed',
  'pool_added',
])

export function isKeyMoment(type: JourneyEventType): boolean {
  return !BACKGROUND_TYPES.has(type)
}

// ─── Tone ─────────────────────────────────────────────────────────────────────

/**
 * Colour by meaning (design system → semantic accents): gold for priority and
 * ranking, forest for done and live, clay for a warning, neutral for an ending,
 * slate for structure.
 */
export function toneFor(type: JourneyEventType): Tone {
  switch (type) {
    case 'session_added':
    case 'promoted':
    case 'leadership_approved':
    case 'decision_recorded':
      return 'mustard'
    case 'completed':
    case 'shaping_completed':
    case 'reactivated':
      return 'green'
    case 'deferred':
    case 'relationship_dissolved':
      return 'coral'
    case 'archived':
    case 'retired':
    case 'lineage_retire':
    case 'lineage_supersede':
    case 'dedupe_reviewed':
      return 'neutral'
    default:
      return 'slate'
  }
}

// ─── State over time ──────────────────────────────────────────────────────────

export interface StateSpan {
  label: string
  tone: Tone
  start: Date
  /** Where the span stops: the next change, or `now` for the state it is in today. */
  end: Date
  /** The item's identity ended here (retired or superseded); nothing follows. */
  ended: boolean
}

/** The state an event puts the item in. Events not listed leave it unchanged. */
const STATE_AFTER: Partial<Record<JourneyEventType, { label: string; tone: Tone; ended?: boolean }>> = {
  created: { label: 'Active', tone: 'slate' },
  imported: { label: 'Active', tone: 'slate' },
  reactivated: { label: 'Active', tone: 'slate' },
  shaping_started: { label: 'In work', tone: 'slate' },
  shaping_completed: { label: 'Ready', tone: 'green' },
  leadership_approved: { label: 'Approved', tone: 'mustard' },
  promoted: { label: 'Promoted', tone: 'mustard' },
  deferred: { label: 'Deferred', tone: 'coral' },
  archived: { label: 'Archived', tone: 'neutral' },
  completed: { label: 'Complete', tone: 'green' },
  retired: { label: 'Retired', tone: 'neutral', ended: true },
  lineage_retire: { label: 'Retired', tone: 'neutral', ended: true },
  lineage_supersede: { label: 'Superseded', tone: 'neutral', ended: true },
}

/**
 * The item's state from its first event to today, as back-to-back spans.
 * Takes every event (not only key moments) in time order.
 */
export function deriveStateSpans(events: JourneyEvent[], now: Date): StateSpan[] {
  if (events.length === 0) return []
  const spans: StateSpan[] = []
  // An item with no recorded creation still existed from its first event.
  let current: StateSpan = { label: 'Active', tone: 'slate', start: events[0].timestamp, end: now, ended: false }

  for (const event of events) {
    const next = STATE_AFTER[event.type]
    if (!next) continue
    if (next.label === current.label) {
      if (next.ended) current.ended = true
      continue
    }
    if (event.timestamp.getTime() > current.start.getTime()) {
      spans.push({ ...current, end: event.timestamp })
    }
    current = { label: next.label, tone: next.tone, start: event.timestamp, end: now, ended: Boolean(next.ended) }
    if (next.ended) break
  }

  // A state that ended the item is a point, not a stretch that runs to today.
  if (current.ended) current.end = current.start
  spans.push(current)
  return spans
}

// ─── Durations ────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000

/** "today", "1 day", "12 days", "3 months", "2 years". */
export function durationInWords(ms: number): string {
  const days = Math.floor(Math.max(ms, 0) / DAY_MS)
  if (days < 1) return 'today'
  if (days === 1) return '1 day'
  if (days < 60) return `${days} days`
  const months = Math.round(days / 30.44)
  if (months < 24) return `${months} months`
  return `${Math.round(days / 365.25)} years`
}

// ─── The story ────────────────────────────────────────────────────────────────

function shortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The rank part of a session's sublabel — "#2 of 5 · 3.0pts" → "#2 of 5". */
export function rankOf(event: JourneyEvent): string | null {
  if (event.type !== 'session_added' || !event.sublabel) return null
  const match = /#\d+ of \d+/.exec(event.sublabel)
  return match ? match[0] : null
}

/**
 * One or two plain sentences: where the item came from, the most recent
 * ranking it was in, the cycles it reached, and the state it is in today.
 * Every clause comes from an event on the map; a clause with no event is left out.
 */
export function journeyStory(events: JourneyEvent[], now: Date): string {
  if (events.length === 0) return ''
  const sentences: string[] = []

  const origin = events.find((e) => e.branch === 'in')
  const born = events.find((e) => e.type === 'created' || e.type === 'imported')
  if (origin) {
    const verb = origin.type === 'lineage_merge' ? 'Merged from' : origin.type === 'lineage_fork' ? 'Forked from' : 'Split from'
    sentences.push(`${verb} ${origin.branchLabel ?? 'another item'} on ${shortDate(origin.timestamp)}.`)
  } else if (born) {
    sentences.push(`${born.type === 'imported' ? 'Imported' : 'Created'} ${shortDate(born.timestamp)}.`)
  }

  const ranked = events.filter((e) => rankOf(e))
  const lastRanked = ranked[ranked.length - 1]
  if (lastRanked) {
    const where = `${rankOf(lastRanked)} in ${lastRanked.label}`
    sentences.push(ranked.length > 1 ? `Ranked in ${ranked.length} sessions, most recently ${where}.` : `Ranked ${where}.`)
  }

  const cycles = events.filter((e) => e.type === 'cycle_added')
  if (cycles.length === 1) sentences.push(`In ${cycles[0].label}.`)
  else if (cycles.length > 1) sentences.push(`In ${cycles.length} cycles.`)

  const spans = deriveStateSpans(events, now)
  const today = spans[spans.length - 1]
  if (today) {
    sentences.push(
      today.ended
        ? `${today.label} ${shortDate(today.start)}.`
        : `${today.label} for ${durationInWords(now.getTime() - today.start.getTime())}.`.replace(' for today.', ' since today.'),
    )
  }
  return sentences.join(' ')
}
